<!-- 
  NavBar Component
  
  Props:
  - isUnifiedApp: boolean - Flag to indicate if this is a unified app
  - r7eApp: Object - R7eApp instance
  - getNavBarData: Function - Function that returns a DefaultNavBarData instance
  
  Usage:
  <NavBar 
    :isUnifiedApp="true" 
    :r7eApp="r7eApp"
    :getNavBarData="() => new MyCustomNavBarData()" 
  />
  
  The getNavBarData function should return an object with:
  - title_id(): string - Translation key for the navbar title
  - title(): string|null - Direct title text (optional)
  - items(): array - Array of navigation items with route, title_id, description, and condition
-->
<template>
  <nav class="navbar">
    <div class="navbar-brand">
      <a
        class="logo title-link"
        :href="service_url('website')"
      ><img
        :src="logoSrc"
        alt="Recognyze"
      ></a>
      <a
        class="title-link"
        href="/"
      >{{ navbar.title_id ? t(navbar.title_id) : (navbar.title ? navbar.title : '') }}</a>
      <button
        class="hamburger"
        @click="toggleMobileMenu"
        aria-label="Toggle navigation"
      >
        <span :class="{ 'open': mobileMenuOpen }" />
        <span :class="{ 'open': mobileMenuOpen }" />
        <span :class="{ 'open': mobileMenuOpen }" />
      </button>
    </div>
    <ul
      class="navbar-links"
      :class="{ 'mobile-open': mobileMenuOpen }"
    >
      <template
        v-for="rt in navbar.items"
        :key="rt.route"
      >
        <li 
          v-if="rt.condition && isFeatureAvailable(rt)"
          :class="{ 'dropdown': hasDescription(rt) }"
        >
          <router-link :to="rt.route">
            <template v-if="rt.icon">
              <i :class="getIconClass(rt.icon)" />
            </template>
            {{ rt.title_id ? t(rt.title_id) : (rt.title ? rt.title : '') }}
          </router-link>
          <div 
            v-if="hasDescription(rt) && r7eApp.isHelpEnabled()"
            class="dropdown-menu description-dropdown"
          >
            <div class="description-content">
              {{ getDescription(rt) }}
            </div>
          </div>
        </li>
      </template>
      <li>
        <router-link to="/docs">
          <i :class="getIconClass('fa-solid fa-book')" />
          {{ t('navbar.docs') }}
        </router-link>
      </li>
      <li
        v-if="isAuthenticated"
        class="notifications-nav-item"
      >
        <NotificationsWidget :r7e-app="props.r7eApp" />
      </li>
          
      <li
        class="dropdown"
        v-if="hasAccessToCurrentService"
      >
        <a
          href="#"
          class="dropdown-trigger"
        ><i class="fas fa-user" /> <span
          class="username"
          v-if="accountUsername"
        >{{ accountName ? accountName : accountUsername }}</span></a>
        <div class="dropdown-menu">
          <router-link to="/account">
            <i class="fas fa-user-circle" /> {{ t('navbar.account') }}
          </router-link>
          <router-link to="/settings">
            <i class="fas fa-cog" /> {{ t('navbar.settings') }}
          </router-link>
          <a
            href="#"
            @click.prevent="logout"
          >
            <i class="fas fa-sign-out-alt" /> {{ t('navbar.logout') }}
          </a>
        </div>
      </li>
      <li v-else>
        <a
          href="#"
          @click.prevent="login"
        ><i class="fas fa-user" /> {{ t('navbar.login') }}</a>
      </li>
      <li v-if="!hasAccessToCurrentService">
        <router-link
          to="/settings"
          class="icon-only-link"
          title="Settings"
        >
          <i class="fas fa-cog" />
        </router-link>
      </li>
      <li>
        <button
          class="theme-toggle"
          @click="toggleTheme"
          :aria-label="theme === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark')"
        >
          <span v-if="theme === 'dark'">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            ><path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" /></svg>
          </span>
          <span v-else>
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            ><circle
              cx="12"
              cy="12"
              r="5"
            /><path d="M12 1v2m0 18v2m11-11h-2M3 12H1m16.95 6.95-1.41-1.41M6.34 6.34 4.93 4.93m12.02 0-1.41 1.41M6.34 17.66l-1.41 1.41" /></svg>
          </span>
        </button>
      </li>
      <!-- Apps Dropdown -->
      <li
        class="dropdown apps-dropdown"
        @mouseenter="onAppsDropdownOpen"
        @focusin="onAppsDropdownOpen"
      >
        <a
          href="#"
          class="dropdown-trigger apps-trigger"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect
              x="3"
              y="3"
              width="7"
              height="7"
            />
            <rect
              x="14"
              y="3"
              width="7"
              height="7"
            />
            <rect
              x="14"
              y="14"
              width="7"
              height="7"
            />
            <rect
              x="3"
              y="14"
              width="7"
              height="7"
            />
          </svg>
          <!-- <span class="apps-label">{{ t('navbar.apps') }}</span> -->
        </a>
        <div class="dropdown-menu apps-menu">
          <template 
            v-for="role in availableAppRoles" 
            :key="role" 
          >
            <div
              class="app-entry" 
              :title="service_url(role)"
              :class="{ 'active': r7eApp.account_type === role, 'disabled': unavailable_services.includes(role) }"
              @click="switch_to_app(role, $event)"
            >            
              <div
                class="app-icon"
                :class="`${role}-icon`"
              >
                <svg
                  v-if="role === 'publisher'"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
                <svg
                  v-else-if="role === 'author'"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z" />
                  <line
                    x1="16"
                    y1="8"
                    x2="2"
                    y2="22"
                  />
                  <line
                    x1="17.5"
                    y1="15"
                    x2="9"
                    y2="6.5"
                  />
                </svg>
                <svg
                  v-else-if="role === 'consumer'"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                  <rect
                    x="8"
                    y="2"
                    width="8"
                    height="4"
                    rx="1"
                    ry="1"
                  />
                </svg>
                <svg
                  v-else
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                  />
                  <path d="M12 6v6l4 2" />
                </svg>
              </div>
              <div class="app-info">
                <div class="app-name">
                  {{ t(`navbar.apps.${role}`) }}
                </div>
                <div
                  class="app-description"
                  v-if="r7eApp.account_type === role"
                >
                  {{ t(`navbar.apps.currentApplicationDesc`) }}
                </div>
                <div
                  class="app-description"
                  v-else-if="unavailable_services.includes(role)"
                >
                  {{ t(`navbar.apps.unavailableServiceDesc`) }}
                </div>
                <div
                  class="app-description"
                  v-else
                >
                  {{ t(`navbar.apps.${role}Desc`) }}
                </div>
              </div>
            </div>
          </template>
          <div 
            class="app-entry" 
            @click="switch_to_app('website', $event)" 
            :title="service_url('website')"
            :class="{ 'disabled': unavailable_services.includes('website') }"
          >
            <div class="app-icon web-icon">
              <!-- Main website icon: globe -->
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                />
                <line
                  x1="2"
                  y1="12"
                  x2="22"
                  y2="12"
                />
                <path d="M12 2a15.3 15.3 0 0 1 0 20" />
                <path d="M12 2a15.3 15.3 0 0 0 0 20" />
              </svg>
            </div>
            <div class="app-info">
              <div class="app-name">
                {{ t('navbar.apps.website') }}
              </div>
              <div
                class="app-description"
                v-if="unavailable_services.includes('website')"
              >
                {{ t('navbar.apps.unavailableServiceDesc') }}
              </div>
              <div
                class="app-description"
                v-else
              >
                {{ t('navbar.apps.websiteDesc') }}
              </div>
            </div>
          </div>
          <div 
            class="app-entry" 
            @click="switch_to_app('portal', $event)" 
            :title="service_url('portal')"
            :class="{ 'disabled': unavailable_services.includes('portal') }"
            v-if="r7eApp.isUIDebugEnabled()"
          >
            <div class="app-icon web-icon">
              <!-- Main portal icon: backend/portal -->
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect
                  x="2"
                  y="3"
                  width="20"
                  height="8"
                  rx="2"
                  ry="2"
                />
                <rect
                  x="2"
                  y="13"
                  width="20"
                  height="8"
                  rx="2"
                  ry="2"
                />
                <line
                  x1="6"
                  y1="7"
                  x2="6"
                  y2="7"
                />
                <line
                  x1="6"
                  y1="17"
                  x2="6"
                  y2="17"
                />
              </svg>
            </div>
            <div class="app-info">
              <div class="app-name">
                {{ t('navbar.apps.portal') }}
              </div>
              <div
                class="app-description"
                v-if="unavailable_services.includes('portal')"
              >
                {{ t('navbar.apps.unavailableServiceDesc') }}
              </div>
              <div
                class="app-description"
                v-else
              >
                {{ t('navbar.apps.portalDesc') }}
              </div>
            </div>
          </div>          
        </div>
      </li>
    </ul>
  </nav>
  <div
    v-if="hasPendingRegistration && $route.path !== '/profile-setup-REMOVE'"
    class="pending-registration-banner"
  >
    {{ t('navbar.pendingRegistrationDesc') }}
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { ref, onMounted, onUnmounted, defineProps, computed } from 'vue';
// import logo from '../assets/logo.png';
import { logoDark } from '@r7e/shared';
import { localStorageManager, DEBUG_MODE } from '@r7e/shared'
import { useI18n } from 'vue-i18n';
// import { useToast } from "primevue/usetoast";
import NotificationsWidget from './NotificationsWidget.vue';

// const toast = useToast();

// Define props
// isUnifiedApp: boolean flag to indicate if this is a unified app (can be used for conditional logic)
// getNavBarData: function that returns a DefaultNavBarData instance with title_id(), title(), and items() methods
const props = defineProps({
  r7eApp: {
    type: Object,
    required: true
  },
  isUnifiedApp: {
    type: Boolean,
    default: false
  },
  getNavBarData: {
    type: Function,
    required: true
  }
});

/* -------------------- */
/* -- start of setup -- */
/* -------------------- */
class DefaultNavBarData {
  title_id() {  return null; }
  title() {  return null; }
  items() {
    const b = props.r7eApp.hasAccessToCurrentService();
    return [
        { route: "/info", title_id: 'navbar.info', condition: b },
    ]
  }
}
/* -------------------- */
/* --- end of setup --- */
/* -------------------- */

/* DO NOT MODIFY THE REST */

const { t } = useI18n();

const router = useRouter();

const isAuthenticated = ref(props.r7eApp.isAuthenticated());
const hasPendingRegistration = ref(props.r7eApp.hasPendingRegistrationForCurrentService());
const hasAccessToCurrentService = ref(props.r7eApp.hasAccessToCurrentService());
const accountUsername = ref();
const accountName = ref();
const mobileMenuOpen = ref(false);

const unavailable_services = ref([]);

const isFeatureAvailable = (rt) => {
  if (rt.experiment) {
    return props.r7eApp.isFeatureAvailable(rt.experiment);
  }
  return true;
}

const hasDescription = (rt) => {
  return (rt.description !== undefined && rt.description !== null && rt.description !== '');
}

const getDescription = (rt) => {
  if (rt.description) {
    return t(rt.description);
  }
  return rt.description || '';
}

// Use the provided function to get navbar data, or fall back to default
const navbarData = props.getNavBarData ? props.getNavBarData() : new DefaultNavBarData();
const navbar = ref({
  title_id: navbarData.title_id(),
  title: navbarData.title(),
  items: navbarData.items()
})

// Get available app roles from r7eApp
const availableAppRoles = computed(() => {
  return props.r7eApp.availableRoles ? props.r7eApp.availableRoles() : ['publisher', 'author', 'consumer'];
});

// Example of how isUnifiedApp could be used for conditional logic
// if (props.isUnifiedApp) {
//   // Apply unified app specific styling or behavior
// }

const on_authentication_event = (data) => {
  isAuthenticated.value = props.r7eApp.isAuthenticated();
  hasAccessToCurrentService.value = props.r7eApp.hasAccessToCurrentService();

  if (DEBUG_MODE) {
    console.log("NavBar, authentication event", { isAuthenticated: isAuthenticated.value, hasAccessToCurrentService: hasAccessToCurrentService.value, data: data });
  }
  // console.trace();
  if (window.__navbarCheckValidAccountTimeout) {
        clearTimeout(window.__navbarCheckValidAccountTimeout);
  }
  hasPendingRegistration.value = props.r7eApp.hasPendingRegistrationForCurrentService();  

  // Re-compute navbar data using the provided function
  const updatedNavBarData = props.getNavBarData ? props.getNavBarData() : navbarData;
  navbar.value.title_id = updatedNavBarData.title_id();
  navbar.value.title = updatedNavBarData.title();
  navbar.value.items = updatedNavBarData.items();
}

// THEME LOGIC
const theme = ref(localStorageManager.getItem('theme') || 'light');

const applyTheme = (t) => {
  const root = document.documentElement;
  root.classList.remove('theme-dark', 'theme-light');
  root.classList.add(`theme-${t}`);
};

const toggleTheme = () => {
  theme.value = theme.value === 'dark' ? 'light' : 'dark';
  localStorageManager.setItem('theme', theme.value);
  applyTheme(theme.value);
};

function handleStorageChange(event) {
  // console.log("handleStorageChange", event);
  if (event.key === 'theme') {
    theme.value = event.newValue === 'dark' ? 'dark' : 'light';
    applyTheme(theme.value);
  }
}

onMounted(async () => {
  // THEME
  const saved = localStorageManager.getItem('theme');
  theme.value = saved === 'dark' ? 'dark' : 'light';
  applyTheme(theme.value);
  localStorageManager.addEventListener(handleStorageChange);

});

onUnmounted(() => {
  localStorageManager.removeEventListener(handleStorageChange);
});

props.r7eApp.registerCredentialsObserver(({ data }) => {
  // console.log("NavBar, credentials observer notified", data);
  accountUsername.value = data?.username || '';
  accountName.value = data?.name || '';
  on_authentication_event (data);
});

const logout = async () => {
  await props.r7eApp.logoutAccount();
  router.push('/login');
};

const login = () => {
  router.push('/login');
};

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

const checkServicesAvailability = async () => {
  // Only check once per open, clear previous state
  unavailable_services.value = [];
  const services = ['website', 'portal'];
  for (const srv of availableAppRoles.value) {
    services.push(srv);
  }
  for (const srv of services) {
    const b = await is_service_available(srv);
    if (!b) {
      unavailable_services.value.push(srv);
    }
  }
}

// Function to switch between different app types
const is_service_available = async (srv) => { 
  return await props.r7eApp.is_service_available(srv);
}

const switch_to_app = (srv, event) => {
  // console.log(`Switching to ${srv} app`);
  
  // Check if Ctrl (Windows/Linux) or Cmd (Mac) key is pressed
  const openInNewTab = event && (event.ctrlKey || event.metaKey);
  
  // You can implement the logic here to switch between different app types
  // For example, redirect to different URLs or trigger app-specific actions
  let url = '';
  switch (srv) {
    case 'website':
      url = service_url ('website');
      if (openInNewTab) {
        window.open(url, '_blank_' + srv);
      } else {
        window.open(url, '_blank_' + srv);
      }
      break;
    case 'portal':
      url = service_url ('portal');
      // console.log(`Switching to ${srv} app: ${url}`);
      if (openInNewTab) {
        window.open(url, '_blank_' + srv);
      } else {
        window.location.href = url;
      }
      break;      
    default:
      url = service_url (srv);
      if (openInNewTab) {
        window.open(url, '_blank_' + srv);
      } else {
        window.location.href = url;
      }
  }
};

const service_url = (srv) => {
  return props.r7eApp.service_url (srv);
}

// Apps dropdown open handler
let appsDropdownChecked = false;
const onAppsDropdownOpen = async () => {
  // Only check if not already checked since last close
  if (!appsDropdownChecked) {
    appsDropdownChecked = true;
    await checkServicesAvailability();
  }
};

// Optionally, reset appsDropdownChecked when mouse leaves dropdown
// (so that next open will re-check)
if (typeof window !== 'undefined') {
  // Use event delegation to reset appsDropdownChecked on mouseleave
  // This is a workaround for Vue's lack of @mouseleave on <li> with dropdown
  setTimeout(() => {
    const dropdown = document.querySelector('.apps-dropdown');
    if (dropdown) {
      dropdown.addEventListener('mouseleave', () => {
        appsDropdownChecked = false;
      });
      dropdown.addEventListener('focusout', (e) => {
        // If focus leaves the dropdown, reset
        if (!dropdown.contains(e.relatedTarget)) {
          appsDropdownChecked = false;
        }
      });
    }
  }, 0);
}

// const logoSrc = computed(() => theme.value === 'dark' ? logoDark : logo);
const logoSrc = logoDark; //computed(() => theme.value === 'dark' ? logoDark : logoDark);

const getIconClass = (icon) => {
  if (!icon) return '';
  
  // Trim whitespace and normalize multiple spaces to single space
  icon = icon.trim().replace(/\s+/g, ' ');
  
  // If icon already contains a space, it's a full class string
  if (icon.includes(' ')) {
    // Normalize Font Awesome 6 format to Font Awesome 5 format
    // This project uses Font Awesome 5 (fas, far, fab) based on other icons in the file
    // fa-solid fa-list -> fas fa-list
    // fa-regular fa-list -> far fa-list
    // fa-brands fa-list -> fab fa-list
    let normalized = icon
      .replace(/^fa-solid\s+fa-/, 'fas fa-')
      .replace(/^fa-regular\s+fa-/, 'far fa-')
      .replace(/^fa-brands\s+fa-/, 'fab fa-')
      .replace(/^fa-light\s+fa-/, 'fal fa-')
      .replace(/^fa-duotone\s+fa-/, 'fad fa-');
    
    // If it's already in FA5 format (fas, far, fab, etc.), return as-is
    if (/^(fas|far|fab|fal|fad)\s+fa-/.test(normalized)) {
      return normalized;
    }
    
    // If normalization didn't change it and it's not FA5 format, try to convert
    // This handles cases like "fa fa-list" -> "fas fa-list"
    if (/^fa\s+fa-/.test(normalized)) {
      return normalized.replace(/^fa\s+fa-/, 'fas fa-');
    }
    
    return normalized;
  }
}
</script>

<style scoped>
a.logo img {
  margin-right: 1rem;
  padding: 2px;
  /* background-color: #fff; */
  /* width: 100px; */
  height: 70px;
}
.navbar {
  background-color: var(--color-navbar-bg);
  color: var(--color-navbar-text);
  padding: 15px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
}
.navbar-brand {
  font-size: 1.5em;
  font-weight: bold;
  display: flex;
  align-items: center;
}
.title-link {
  color: var(--color-navbar-text);
  text-decoration: none;
  transition: color 0.3s;
}
.title-link:hover {
  color: var(--color-green);
}
.hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  margin-left: 1rem;
  cursor: pointer;
  z-index: 20;
}
.hamburger span {
  display: block;
  height: 4px;
  width: 100%;
  background: var(--color-text-light);
  margin: 4px 0;
  border-radius: 2px;
  transition: 0.3s;
}
.hamburger span.open:nth-child(1) {
  transform: translateY(8px) rotate(45deg);
}
.hamburger span.open:nth-child(2) {
  opacity: 0;
}
.hamburger span.open:nth-child(3) {
  transform: translateY(-8px) rotate(-45deg);
}

.navbar-links {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
}
.navbar-links li {
  margin-left: 20px;
  position: relative;
}
.navbar-links a {
  color: var(--color-navbar-text);
  text-decoration: none;
  transition: color 0.3s;
}
.navbar-links a:hover,
.navbar-links a.router-link-exact-active {
  color: var(--color-green);
}

.dropdown {
  position: relative;
}

.dropdown-menu {
  display: none;
  position: absolute;
  top: 100%;
  right: 0;
  background-color: color-mix(in srgb, var(--color-navbar-bg), black 10%);
  min-width: 160px;
  box-shadow: 0 8px 16px rgba(0,0,0,0.2);
  z-index: 1;
  border-radius: 0 0 4px 4px;
  border: 1px solid color-mix(in srgb, var(--color-border), transparent 50%);
  border-top: none;
  padding-top: 4px;
}

.dropdown-menu a {
  color: var(--color-navbar-text);
  padding: 12px 16px;
  text-decoration: none;
  display: block;
}

.dropdown-menu a:hover {
  background-color: var(--color-green);
  color: var(--color-navbar-text);
}

.dropdown:hover .dropdown-menu {
  display: block;
}

/* Description Dropdown Specific Styles */
.description-dropdown {
  min-width: 200px;
  max-width: 300px;
  padding: 12px 16px;
  left: 0;
  right: auto;
}

.description-content {
  color: color-mix(in srgb, var(--color-navbar-text), transparent 20%);
  font-style: italic;
  font-size: 0.8em;
  line-height: 1.4;
  white-space: normal;
}

/* Apps Dropdown Specific Styles */
.apps-dropdown .dropdown-menu {
  min-width: 280px;
  padding: 8px 0;
}

.apps-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.3s;
}

.apps-trigger:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.apps-label {
  font-size: 0.9em;
  font-weight: 500;
}

.app-entry.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: var(--color-disabled-bg);
  color: var(--color-disabled-text);
}
.app-entry.active {
  background-color: var(--color-disabled-bg);
  color: var(--color-disabled-text);
}

.app-entry {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.3s;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.app-entry:last-child {
  border-bottom: none;
}

.app-entry:hover {
  background-color: var(--color-green);
  color: var(--color-navbar-text);
}

.app-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  background-color: rgba(255, 255, 255, 0.1);
}

.publisher-icon {
  background-color: rgba(52, 152, 219, 0.2);
  color: #3498db;
}

.author-icon {
  background-color: rgba(155, 89, 182, 0.2);
  color: #9b59b6;
}

.consumer-icon {
  background-color: rgba(46, 204, 113, 0.2);
  color: #2ecc71;
}

.app-info {
  flex: 1;
}

.app-name {
  font-weight: 600;
  font-size: 0.95em;
  margin-bottom: 2px;
}

.app-description {
  font-size: 0.8em;
  opacity: 0.8;
  line-height: 1.3;
}

.username {
  color: var(--color-gold);
  font-weight: bold;
}

.theme-toggle {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-light);
  font-size: 1.3em;
  margin-left: 10px;
  display: flex;
  align-items: center;
  transition: color 0.3s;
}
.theme-toggle:hover {
  color: var(--color-green);
}

.lang-switcher {
  background-color: var(--color-navbar-bg);
  color: var(--color-navbar-text);
  border: 1px solid var(--color-text-light);
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 0.9em;
  cursor: pointer;
  transition: background-color 0.3s, border-color 0.3s;
}
.lang-switcher:hover {
  background-color: var(--color-green);
  border-color: var(--color-green);
}
.pending-registration-banner {
  font-size: 1.2rem;
  padding: 2rem;;
  margin-bottom: 10px;
  background-color: var(--color-required-bg);
  color: var(--color-required-text);
}

.notifications-nav-item {
  display: flex;
  align-items: center;
}

@media (max-width: 900px) {
  .navbar {
    flex-direction: column;
    align-items: flex-start;
    padding: 10px 15px;
  }
  .navbar-brand {
    width: 100%;
    justify-content: space-between;
  }
  .hamburger {
    display: flex;
  }
  .navbar-links {
    flex-direction: column;
    width: 100%;
    background: var(--color-dark-blue);
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    display: none;
    box-shadow: 0 8px 16px rgba(0,0,0,0.2);
    border-radius: 0 0 8px 8px;
    z-index: 10;
  }
  .navbar-links.mobile-open {
    display: flex;
  }
  .navbar-links li {
    margin: 0;
    width: 100%;
    text-align: left;
    border-bottom: 1px solid rgba(255,255,255,0.1);
  }
  .dropdown-menu {
    position: static;
    box-shadow: none;
    border-radius: 0;
    background: none;
  }
  .dropdown:hover .dropdown-menu {
    display: block;
  }
  
  /* Mobile Description Dropdown */
  .description-dropdown {
    position: static;
    box-shadow: none;
    border-radius: 0;
    background: rgba(255, 255, 255, 0.05);
    margin-top: 8px;
    margin-left: 16px;
    max-width: none;
  }
  
  /* Mobile Apps Dropdown */
  .apps-dropdown .dropdown-menu {
    min-width: auto;
    width: 100%;
  }
  
  .app-entry {
    padding: 16px;
  }
  
  .app-icon {
    width: 36px;
    height: 36px;
  }
}
</style>